/**
 * Unified Account read layer.
 *
 * Reads `companies` (migrated), `customers`, and `vendors` from the MFX-OS
 * Firestore and normalizes everything into one `Account` view model.
 * READS ONLY — nothing here writes to production.
 */
import { collection, getDocs, query, limit } from 'firebase/firestore';
import { db } from './firebase';
import { DEMO_ACCOUNTS } from './demoData';
import type { Customer, Address } from '@mfx/contracts';

// Account is the CRM's view model built on the shared @mfx/contracts Customer:
// every Account satisfies Customer (id/name/type/status/contact/addresses/tags/
// notes/…), with CRM-only fields (provenance, vendor metadata, health) layered
// on top. Type/status/address shapes come from the contract so they stay in sync.
export type AccountType = Customer['type'];      // 'client' | 'vendor' | 'both'
export type AccountStatus = Customer['status'];  // 'active' | 'prospect' | 'inactive'
export type AccountAddress = Address;

export interface Account extends Customer {
  source?: string;
  fax?: string;
  rating?: string;
  recordId?: string;
  category?: string;        // vendor: supply category
  materials?: string[];     // vendor: supplied materials
  avgLeadDays?: number;     // vendor: avg lead time
  healthScore?: number;
  mfxId?: string;           // MFX-C#### — the one id per company on `customers` (b718mid, 2026-10-25); twin docs share it
  _origin: 'customers' | 'vendors' | 'companies' | 'demo';
}

export interface AccountLoad {
  accounts: Account[];
  live: boolean;
  sources: string[];
}

const str = (v: unknown): string | undefined => {
  if (v == null) return undefined;
  const s = String(v).trim();
  return s.length ? s : undefined;
};

function fromCustomer(id: string, d: Record<string, any>): Account {
  return {
    id: `cust_${id}`,
    // the 410 c-number docs (c1001…, the Zoho account id carriers) name the company in `company`, not `name` — without
    // that fallback they all listed as "Unnamed account" (b726crm, 2026-10-26)
    name: str(d.name) || str(d.company) || str(d.accountName) || str(d['Account Name']) || 'Unnamed account',
    mfxId: str(d.mfxId),
    type: 'client',
    status: d.deleted ? 'inactive' : 'active',
    source: str(d.source) || 'os',
    industry: str(d.industry) || str(d.Industry),
    website: str(d.website) || str(d.Website),
    phone: str(d.phone) || str(d.Phone),
    fax: str(d.fax) || str(d.Fax),
    rating: str(d.rating) || str(d.Rating),
    recordId: str(d.recordId) || str(d['Record Id']),
    contactName: str(d.contactName) || str(d.contact) || str(d.Contact) || str(d.primaryContact),
    email: str(d.email) || str(d.Email),
    healthScore: typeof d.healthScore === 'number' ? d.healthScore : undefined,
    billing: {
      street: str(d.billingStreet) || str(d['Billing Street']), city: str(d.billingCity) || str(d['Billing City']),
      state: str(d.billingState) || str(d['Billing State']), postal: str(d.billingCode) || str(d['Billing Code']),
      country: str(d.billingCountry) || str(d['Billing Country']),
    },
    shipping: {
      street: str(d.shippingStreet) || str(d['Shipping Street']), city: str(d.shippingCity) || str(d['Shipping City']),
      state: str(d.shippingState) || str(d['Shipping State']), postal: str(d.shippingCode) || str(d['Shipping Code']),
      country: str(d.shippingCountry) || str(d['Shipping Country']),
    },
    tags: Array.isArray(d.tags) ? d.tags : undefined,
    _origin: 'customers',
  };
}

function fromVendor(id: string, d: Record<string, any>): Account {
  const mats = Array.isArray(d.materials)
    ? d.materials.map((m: any) => (typeof m === 'string' ? m : m?.name)).filter(Boolean)
    : (str(d.material) ? [str(d.material)!] : undefined);
  return {
    id: `vend_${id}`,
    name: str(d.name) || 'Unnamed vendor',
    type: 'vendor',
    status: d.deleted ? 'inactive' : 'active',
    category: str(d.category),
    materials: mats,
    avgLeadDays: typeof d.avgLeadDays === 'number' ? d.avgLeadDays : undefined,
    contactName: str(d.contact) || (Array.isArray(d.contacts) ? str(d.contacts[0]?.name) : undefined),
    email: str(d.email),
    phone: str(d.phone),
    healthScore: typeof d.lastHealthScore === 'number' ? d.lastHealthScore : undefined,
    notes: str(d.notes),
    _origin: 'vendors',
  };
}

function fromCompany(id: string, d: Record<string, any>): Account {
  const a = (d.addresses && d.addresses[0]) || {};
  return {
    id,
    name: str(d.name) || 'Unnamed',
    type: (d.type as AccountType) || 'client',
    status: (d.status as AccountStatus) || 'active',
    industry: str(d.industry),
    source: str(d.source),
    website: str(d.website),
    phone: str(d.phone),
    healthScore: typeof d.health_score === 'number' ? d.health_score : undefined,
    tags: Array.isArray(d.tags) ? d.tags : undefined,
    billing: a.line1 ? { street: a.line1, city: a.city, state: a.region, postal: a.postal, country: a.country } : undefined,
    _origin: 'companies',
  };
}

async function readCollection(name: string, mapper: (id: string, d: Record<string, any>) => Account): Promise<Account[]> {
  const snap = await getDocs(query(collection(db, name), limit(2000)));
  return snap.docs.map((doc) => mapper(doc.id, doc.data() as Record<string, any>));
}

export async function loadAccounts(): Promise<AccountLoad> {
  const sources: string[] = [];
  const accounts: Account[] = [];
  await Promise.all([
    readCollection('companies', fromCompany).then((r) => { if (r.length) { accounts.push(...r); sources.push(`companies (${r.length})`); } }).catch(() => {}),
    readCollection('customers', fromCustomer).then((r) => { if (r.length) { accounts.push(...r); sources.push(`customers (${r.length})`); } }).catch(() => {}),
    readCollection('vendors', fromVendor).then((r) => { if (r.length) { accounts.push(...r); sources.push(`vendors (${r.length})`); } }).catch(() => {}),
  ]);

  const migratedNames = new Set(accounts.filter((a) => a._origin === 'companies').map((a) => a.name.toLowerCase()));
  const deduped = accounts.filter((a) => a._origin === 'companies' || !migratedNames.has(a.name.toLowerCase()));

  if (deduped.length === 0) return { accounts: DEMO_ACCOUNTS, live: false, sources: ['demo dataset'] };
  deduped.sort((a, b) => a.name.localeCompare(b.name));
  return { accounts: deduped, live: true, sources };
}

export function loadDemo(): AccountLoad {
  return { accounts: DEMO_ACCOUNTS, live: false, sources: ['demo dataset'] };
}

export const fullAddress = (a?: AccountAddress): string => {
  if (!a) return '';
  return [a.street, a.city, a.state, a.postal, a.country].filter(Boolean).join(', ');
};
export const shortLocation = (a: Account): string => {
  const ad = a.billing?.city ? a.billing : a.shipping;
  return [ad?.city, ad?.state].filter(Boolean).join(', ') || '—';
};

// ── Contacts derived from accounts ────────────────────────────
export interface Contact {
  id: string;
  name: string;
  accountId: string;
  accountName: string;
  accountType: AccountType;
  email?: string;
  phone?: string;
  industry?: string;
  location: string;
}

export function contactsFromAccounts(accounts: Account[]): Contact[] {
  return accounts
    .filter((a) => a.contactName)
    .map((a) => ({
      id: `${a.id}__contact`,
      name: a.contactName!,
      accountId: a.id,
      accountName: a.name,
      accountType: a.type,
      email: a.email,
      phone: a.phone,
      industry: a.industry,
      location: shortLocation(a),
    }))
    .sort((x, y) => x.name.localeCompare(y.name));
}
